import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import { ProfileEditor } from "@/components/ProfileEditor";
import { MemberLogin } from "@/components/MemberLogin";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

export default function FamilyProfiles() {
  const { user, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [manage, setManage] = useState(false);
  const [editing, setEditing] = useState(null);
  const profiles = user.profiles || [];
  if (user.is_family_member) {
    return (
      <main data-testid="family-member-note" className="max-w-xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-extrabold">You're on your own login</h1>
        <p className="text-slate-400 mt-3">Your login is linked to your family plan, so you share the family trip plan and hearts. Profiles are managed from the main family login.</p>
      </main>
    );
  }

  const choose = async (p) => {
    if (manage) return setEditing(p);
    try { const { data } = await api.post("/family/select", { profile_id: p.id }); setUser(data); navigate("/explore"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <main data-testid="family-profiles" className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">{manage ? "Manage profiles" : "Who's exploring?"}</h1>
      <p className="text-slate-400 mt-3">{user.account_name} · Family plan · up to 5 profiles</p>
      <div className="mt-12 flex flex-wrap justify-center gap-8">
        {profiles.map((p, i) => (
          <button key={p.id} data-testid={`family-profile-${p.id}`} onClick={() => choose(p)} style={{ animationDelay: `${i * 60}ms` }}
            className="rise group w-28 flex flex-col items-center gap-3">
            <span className="relative transition-transform duration-200 group-hover:scale-105">
              <Avatar url={p.avatar_url} name={p.name} size="h-28 w-28" text="text-4xl" className="!rounded-3xl ring-2 ring-transparent group-hover:ring-white" />
              {manage && <span className="absolute inset-0 rounded-3xl bg-[#0B0F19]/60 grid place-items-center"><Pencil className="h-6 w-6" /></span>}
            </span>
            <span className="text-sm font-semibold flex items-center gap-1 text-slate-300 group-hover:text-white">{p.name}{p.private_hearts && <Lock className="h-3 w-3 text-slate-500" />}</span>
          </button>
        ))}
        {profiles.length < 5 && (
          <button data-testid="family-add-profile" onClick={() => setEditing(false)} className="group w-28 flex flex-col items-center gap-3">
            <span className="h-28 w-28 rounded-3xl border-2 border-dashed border-[#26334D] grid place-items-center text-slate-500 group-hover:text-white group-hover:border-slate-400 transition-colors"><Plus className="h-8 w-8" /></span>
            <span className="text-sm font-semibold text-slate-400">Add profile</span>
          </button>
        )}
      </div>
      {profiles.length > 0 && (
        <button data-testid="family-manage-toggle" onClick={() => setManage(!manage)}
          className="mt-14 px-6 h-11 rounded-full border border-[#26334D] text-sm font-semibold text-slate-300 hover:text-white hover:border-slate-400 transition-colors">
          {manage ? "Done" : "Manage profiles"}
        </button>
      )}
      {!manage && <MemberLogin profile={profiles.find((p) => p.id === user.active_profile?.id)} onCreated={refreshUser} />}
      <ProfileEditor open={editing !== null} profile={editing || null} onClose={() => setEditing(null)} onSaved={refreshUser} />
    </main>
  );
}
